// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@plumberssupply/MobiusOverrides/Button/Button";
import Clickable from "@plumberssupply/MobiusOverrides/Clickable/Clickable";
import { LazyPicture } from "@plumberssupply/MobiusOverrides/LazyImage";
import { color, ellipsis, xScreen } from "@plumberssupply/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    mediumImage = "mediumImage",
    smallImage = "smallImage",
    alt = "alt",
    title = "title",
    description = "description",
    link = "link",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    buttonLabel = "buttonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.mediumImage]: string;
        [fields.smallImage]: string;
        [fields.alt]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.buttonLabel]: string;
    };
}

type Props = OwnProps;

interface ContentAreaCardStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss;
    container?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    textWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
}

const ContentAreaCardStyles: ContentAreaCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    content: {
        css: css`
            width: 100%;
            border: 1px solid ${color("common.ui7")};
            background-color: ${color("common.ui5")};
            margin-bottom: 29px;
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
            }
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            height: 100%;
            margin-bottom: 16px;
        `,
    },
    image: {
        width: "100%",
        height: "160px",
        css: css`
            max-height: 160px;
            ${xScreen("lte", "767")} {
                max-height: 82px;
            }
            img {
                width: 100%;
                height: 100%;
            }
            border-bottom: 4px solid ${color("primary.main")};
        `,
    },
    title: {
        as: "h3",
        size: 24,
        lineHeight: "32px",
        color: "common.ui11",
        weight: 800,
        css: css`
            margin-bottom: 12px;
            ${ellipsis(1)}
            ${xScreen("lte", "767")} {
                ${ellipsis(2)}
            }
        `,
    },
    description: {
        as: "p",
        size: 14,
        lineHeight: "22px",
        color: "text.main",
        weight: "normal",
        css: css`
            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                color: inherit;
                margin-bottom: 0;
            }
        `,
    },
    textWrapper: {
        css: css`
            flex-grow: 1;
            padding: 24px 24px 0 24px;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-top: auto;
            padding: 0 24px 24px 24px;
        `,
    },
};

const styles = ContentAreaCardStyles;

const ContentAreaCard: React.FC<Props> = ({
    fields: { image, mediumImage, smallImage, alt, title, description, buttonLabel, buttonLink, buttonVariant },
}) => {
    const history = useHistory();
    const link = useGetLink(buttonLink);

    return (
        <StyledWrapper data-test-selector="plumberssupply-ContentAreaCard">
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.content}>
                    <StyledWrapper {...styles.container} data-test-selector={`ContentCard-${title}`}>
                        <LazyPicture
                            largeImageSrc={image}
                            mediumImageSrc={mediumImage}
                            smallImageSrc={smallImage}
                            altText={alt}
                            {...styles.image}
                        />
                        <StyledWrapper {...styles.textWrapper}>
                            <Typography {...styles.title}>{title}</Typography>
                            <Typography {...styles.description}>{parse(description, parserOptions)}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.buttonWrapper}>
                            {buttonLabel && (
                                <Clickable href={link?.url}>
                                    <Button className="slide-button" sizeVariant="medium" variant={buttonVariant}>
                                        {buttonLabel}
                                    </Button>
                                </Clickable>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentAreaCard,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: "image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "mediumImage",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "smallImage",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "alt",
                displayName: "Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false,
                fieldType: "General",
            },
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: "description",
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
            {
                name: "buttonLabel",
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "buttonLink",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: "buttonVariant",
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
